import { useMemo } from 'react'
import {
  getTrackingRestrictedCompanyIds,
  ROUTES,
  VAPermissions,
} from '../Utils/constants'
import { t } from 'i18next'
import orderTrackingIcon from 'Assets/Images/Navbar/orderTracking.svg'
import searchOrderIcon from 'Assets/Images/Navbar/search.svg'
import dashboardTrackingIcon from 'Assets/Images/Navbar/dashboard.svg'
import settingsIcon from 'Assets/Images/Navbar/settings.svg'
import yappyIcon from 'Assets/Images/Navbar/yappy.svg'
import displayIcon from 'Assets/Images/Navbar/display.svg'
import pickingFlowIcon from 'Assets/Images/Navbar/box.svg'
import quickListIcon from 'Assets/Images/Navbar/list-task-settings.svg'
import { useSelector } from 'react-redux'
import useValidatePermission from 'Hooks/useValidatePermission'

// IDs de compañías que tienen restricción de tracking
const TRACKING_RESTRICTED_COMPANY_IDS = getTrackingRestrictedCompanyIds()

const useHandleElements = ({ defaultElements }) => {
  const companyId = useSelector(state => state.company.CompanyId)
  const hasYappyPermission = useValidatePermission(VAPermissions.settingsYappy)
  const hasCustomerDisplayPermission = useValidatePermission(
    VAPermissions.settingsCustomerDisplay
  )
  const hasPickingFlowPermission = useValidatePermission(
    VAPermissions.settingsPickingFlow
  )
  const hasQuickListSettingsPermission = useValidatePermission(
    VAPermissions.settingsQuickList
  )

  // Verificar si la compañía actual tiene acceso al tracking
  const hasTrackingAccess = useMemo(
    () => !TRACKING_RESTRICTED_COMPANY_IDS.includes(companyId),
    [companyId]
  )

  return useMemo(() => {
    const baseElements = [...defaultElements]

    if (hasTrackingAccess) {
      const alreadyIncluded = baseElements.some(
        el => el.title === t('DISPATCH_ORDER_T.ORDER_TRACKING')
      )
      if (!alreadyIncluded) {
        const newElement = {
          title: t('DISPATCH_ORDER_T.ORDER_TRACKING'),
          image: orderTrackingIcon,
          id: 'order-tracking',
          children: [
            {
              grandChildren: [
                {
                  title: 'Búsqueda de pedidos',
                  image: searchOrderIcon,
                  link: ROUTES.orderTracking,
                  id: 'search-orders',
                },
                {
                  title: 'Panel de Control',
                  image: dashboardTrackingIcon,
                  link: ROUTES.dashboardTracking,
                  id: 'dashboard-tracking',
                },
              ],
            },
          ],
        }
        const insertIndex = baseElements.findIndex(
          el => el.title === 'Clientes'
        )
        if (insertIndex === -1) {
          baseElements.push(newElement)
        } else {
          baseElements.splice(insertIndex, 0, newElement)
        }
      }
    }

    const hasAnySettingsPermission =
      hasYappyPermission ||
      hasCustomerDisplayPermission ||
      hasPickingFlowPermission ||
      hasQuickListSettingsPermission

    if (hasAnySettingsPermission) {
      const children = []

      if (hasYappyPermission) {
        children.push({
          title: t('SETTINGS_T.PAYMENT_METHODS'),
          grandChildren: [
            {
              title: t('SETTINGS_T.YAPPY'),
              image: yappyIcon,
              link: ROUTES.yappyConfig,
              id: 'yappy',
            },
          ],
        })
      }

      if (hasCustomerDisplayPermission) {
        children.push({
          title: t('SETTINGS_T.CUSTOMER_DISPLAY.MENU'),
          grandChildren: [
            {
              title: t('SETTINGS_T.CUSTOMER_DISPLAY.SUB_MENU'),
              image: displayIcon,
              link: ROUTES.customerDisplaySettings,
              id: 'customerDisplay',
            },
          ],
        })
      }

      if (hasPickingFlowPermission) {
        children.push({
          title: t('SETTINGS_T.PICKING_FLOW.MENU'),
          grandChildren: [
            {
              title: t('SETTINGS_T.PICKING_FLOW.SUB_MENU'),
              image: pickingFlowIcon,
              link: ROUTES.pickingFlowSettings,
              id: 'pickingFlow',
            },
          ],
        })
      }

      if (hasQuickListSettingsPermission) {
        children.push({
          title: t('SETTINGS_T.QUICK_LIST'),
          grandChildren: [
            {
              title: t('SETTINGS_T.QUICK_LIST'),
              image: quickListIcon,
              link: ROUTES.quickListSettings,
              id: 'quick-list-settings',
            },
          ],
        })
      }

      if (children.length > 0) {
        baseElements.push({
          title: t('SETTINGS_T.TITLE'),
          image: settingsIcon,
          id: 'settings',
          children,
        })
      }
    }

    return baseElements
  }, [
    defaultElements,
    hasTrackingAccess,
    hasYappyPermission,
    hasCustomerDisplayPermission,
    hasPickingFlowPermission,
    hasQuickListSettingsPermission,
  ])
}

export default useHandleElements
